import React, { useEffect, useState } from "react";
import getConfig from "../../config";
import { useAuth } from "providers/CustomAuthProvider";
import {
  Container,
  Row,
  Col,
  Card,
  Image,
  Badge,
  Form,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMedal, faPoo } from "@fortawesome/free-solid-svg-icons";
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import "./RankSummary.css";
import { buildCompanyLogoUrl } from "utils/logo";
import loader from "../../assets/images/datahub-loader.svg";
import delta from "../../assets/images/delta.svg";
import "../../assets/scss/custom/pages/_dashboard.scss";

import {
  fetchLeaderboardParsed,
  fetchBestMarketsParsed,
  fetchWorstMarketsParsed,
  fetchLicenses,
  buildOfNLookup,
  fetchFollowedCompanies,
} from "./api/index";
import { fetchSuppliersListApi } from "../pricing/api";
import LicenseList from "./components/licenseList";
import FollowedEvents from "./components/FollowedEvents";
import Select from "components/UnifiedSelect";

const electricityIconStyle = { color: "#ffc107" };
const gasIconStyle = { color: "#0acf97" };

/* ===== Supplier ID -> domain map (unchanged) ===== */
const supplierLogos = {
  "C4645DA4-BA8F-4973-9AAE-532FB0505B07": "aepenergy.com",
  "32A9E1A8-DF19-4B70-8793-0D1695FD90DF": "ammper.com",
  "64304CDD-0011-4A7E-B0E5-147C9FF78675": "apge.com",
  "6D13B50B-3260-4279-A3B5-1D7E68091ABB": "atlanticenergyco.com",
  "F232C168-5F59-4C6B-B80B-7E0EE34A259E": "branchenergy.com",
  "EE091DE1-695B-4257-B491-688FEC24D852": "catalystpower.com",
  "21043EB4-B9A4-4BBC-9CAD-26C15DFE25EC": "championenergyservices.com",
  "41C9C9C8-F2E6-4B82-BDBE-C55243D41803": "chariotenergy.com",
  "6CE62215-20C0-434E-8AF6-4AF301016DDB": "cleanskyenergy.com",
  "875C7031-92CB-4ABE-811F-1733A049EBF4": "constellation.com",
  "60BB3692-528D-4B1A-8AF9-07E46F55B2B4": "directenergy.com",
  "A837B820-C9CF-4295-8275-E351445959A4": "dynegy.com",
  "6BD2C20F-0CE3-4B00-ACDE-63569A4273A1": "engie.com",
  "702D1F26-0DDC-4A61-AC9F-62C96FE1C136": "firstpointpower.com",
  "958A3FC6-21D4-430F-8834-209E801A7C39": "freepoint.com",
  "3277A3E0-95BC-425C-B729-CC7AE6DF81AD": "gassouth.com",
  "3B0CD20F-FB31-4797-B510-185F51A0DFA1": "genieretail.com",
  "D2BE4A8A-780F-4DB1-9EDC-760E9F3222DD": "gexaenergy.com",
  "052A4363-A6F9-4EF4-9303-B0580FCCE290": "homefieldenergy.com",
  "120DE132-5154-44C1-BC5E-42AEC6ED6909": "hudsonenergy.net",
  "3BAE837B-8BC7-4574-A32F-4AD870C6580D": "indraenergy.com",
  "D1255099-73AB-4D12-B466-542D75DD221B": "ironhorsepowerservices.com",
  "8F63A33F-41DB-4129-B0B4-1A59370D0403": "newwaveenergy.com",
  "FF2AAC3F-BDC8-4E19-AB14-16E2D2A55573": "nexteraenergy.com",
  "C9BC8032-1424-4E15-86B7-8BCD426682BB": "nordicenergy-us.com",
  "38E36FC8-0C9A-4774-9632-555E6C28BB37": "nrg.com",
  "02E684F6-4291-4B71-A37D-C03382FE38E2": "santannaenergyservices.com",
  "A638D4CA-A76A-4861-B1C1-996796FAE4F6": "sfeenergy.com",
  "9FEDD1C8-EB97-404C-9E3C-7A267473CCD4": "sparkenergy.com",
  "374BD0E8-60AC-4A6A-BE86-EF7191E47047": "spragueenergy.com",
  "63A4EA14-1C3C-440A-B764-A070BE7C93A7": "summerenergy.com",
  "9C5337FD-2030-4510-BD57-C736BFDD8E35": "txu.com",
};

const getDomain = (supplierId, fallback = "nrg.com") => {
  const key = (supplierId || "").toUpperCase();
  return supplierLogos[key] || fallback;
};





const SupplierLogo = ({ supplierObject: { supplierId, name }, fallback = "nrg.com", size = 45, className = "" }) => {
  const domain = getDomain(supplierId, fallback);

  const idSafe = (supplierId || domain || "unknown").replace(/[^a-z0-9]/gi, "");
  const overlay = <Tooltip id={`tt-${idSafe}`}>{name}</Tooltip>;
  const containerEl = typeof document !== "undefined" ? document.body : undefined;

  return (
    <OverlayTrigger placement="bottom" overlay={overlay} container={containerEl}>
      <Image
        src={buildCompanyLogoUrl(domain)}
        className={`${size === 50 ? "logo-50" : "logo-45"} ${className}`}
        alt={domain}
        onError={(e) => {
          e.currentTarget.src = `https://via.placeholder.com/${size}?text=?`;
        }}
      />
    </OverlayTrigger>
  );
};

const getCommodityIcon = (commodity) =>
  commodity === "Electricity" ? (
    <ElectricityIcon className="commodity-icon" style={electricityIconStyle} />
  ) : (
    <GasIcon className="commodity-icon" style={gasIconStyle} />
  );

/** Best/Worst pill card (unchanged look/feel) */


const getBestBetterValues = (item) => {
  const bestDelta = Number(item.BestDelta ?? 0);
  const nextBest = Number(item.NextBetterPrice ?? 0);
  const worstDelta = Number(item.WorstDelta ?? 0);
  const nextWorst = Number(item.NextWorsePrice ?? 0);

  // Basic dollar formatter
  const formatDollar = (v) => `$${v.toFixed(6)}`;

  // Explicit + sign for Best / Next Best
  const formatSignedDollar = (v) => {
    const abs = Math.abs(v).toFixed(6);
    // For Best / Next Best always show a leading + for zero or positive values
    if (v >= 0) return `+ $${abs}`;
    return `- $${abs}`;
  };

  // Explicit - sign for Worst / Next Worst
  const formatNegativeDollar = (v) => {
    const abs = Math.abs(v).toFixed(6);
    if (v === 0) return `$${abs}`;
    return `- $${abs}`;
  };

  // Individual metric component for consistent styling
  const MetricItem = ({ icon, label, value, valueClass, formattedValue }) => (
    <div className="d-flex align-items-center gap-1">
      <div
        style={{
          width: 14,
          height: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <small className="text-muted">{label}</small>
      <span className="fw-semibold fs-6">
        {formattedValue != null ? formattedValue : formatDollar(value)}
      </span>
    </div>
  );

  // Use a responsive CSS grid:
  // - On wide screens, up to 2 items per row (Best/Next Best on top, Worst/Next Worst below)
  // - On smaller screens, items stack as space becomes constrained
  return (
    <div
      className="w-100"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        columnGap: "16px",
        rowGap: "0px",
      }}
    >
      <MetricItem
        icon={
          <img
            src={delta}
            alt="Best delta"
            style={{ width: 14, height: 14, display: "block" }}
          />
        }
        label="Best"
        value={bestDelta}
        formattedValue={formatSignedDollar(bestDelta)}
        valueClass={
          bestDelta > 0
            ? "text-success"
            : bestDelta < 0
              ? "text-danger"
              : "text-muted"
        }
      />
      <MetricItem
        icon={
          <img
            src={delta}
            alt="Next best delta"
            style={{ width: 14, height: 14, display: "block" }}
          />
        }
        label="Next Best"
        value={nextBest}
        formattedValue={formatSignedDollar(nextBest)}
        valueClass={
          nextBest > 0
            ? "text-success"
            : nextBest < 0
              ? "text-danger"
              : "text-muted"
        }
      />
      <MetricItem
        icon={
          <img
            src={delta}
            alt="Worst delta"
            style={{ width: 14, height: 14, display: "block" }}
          />
        }
        label="Worst"
        value={worstDelta}
        formattedValue={formatNegativeDollar(worstDelta)}
        valueClass={
          worstDelta < 0
            ? "text-success"
            : worstDelta > 0
              ? "text-danger"
              : "text-muted"
        }
      />
      <MetricItem
        icon={
          <img
            src={delta}
            alt="Next worst delta"
            style={{ width: 14, height: 14, display: "block" }}
          />
        }
        label="Next Worst"
        value={nextWorst}
        formattedValue={formatNegativeDollar(nextWorst)}
        valueClass={
          nextWorst < 0
            ? "text-success"
            : nextWorst > 0
              ? "text-danger"
              : "text-muted"
        }
      />
    </div>
  );
};

const MarketPillCard = ({ item, type }) => {

  const percentChange = Math.round(
    (item.NextWorsePrice / item.CurrPrice) * 100
  );

  // Helper to get whole-number percent, with minimum of 1% when between 0 and 1
  const toWholePercent = (delta) => {
    if (delta == null || !item.CurrPrice) return null;
    const raw = (Math.abs(delta) / item.CurrPrice) * 100;
    if (raw === 0) return 0;
    // Use ceiling so anything between 0 and 1 becomes 1, 1.1 becomes 2, etc.
    return raw < 1 ? 1 : Math.ceil(raw);
  };

  // Percent vs best and worst
  const bestPercent = toWholePercent(item.BestDelta);
  const worstPercent = toWholePercent(item.WorstDelta);

  return (
    <Card
      className={`${type === "BEST" ? "border-Best" : "border-Worst"
        } shadow-sm mb-3 marketPillCard`}
    >
      <Card.Body className="card-body-custom">
        <Row className="align-items-start g-1 g-md-2">
          {/* SECTION 1: Ranking (#1 and of 5) */}
          <Col xs={12} md={2}>
            <div className="d-flex flex-column align-items-center align-items-md-start text-center text-md-start gap-1">
              <span className="fw-bolder fs-4">#{item.rank}</span>
              {item.ofN && (
                <span className="text-muted fs-6">of {item.ofN}</span>
              )}
            </div>
          </Col>

          {/* SECTION 2: Utility Name and Buffer */}
          <Col xs={12} md={4}>
            <div className="d-flex flex-column gap-1">
              {/* Utility */}
              <div className="d-flex align-items-center gap-1">
                <span className="icon-container">
                  {getCommodityIcon(item.commodity)}
                </span>
                <p className="mb-0 fw-bolder fs-6 fs-md-5">
                  {item.utility}
                </p>
              </div>

              {/* Relative performance text */}
              <div className="d-flex flex-column">
                {item.rank !== 1 && bestPercent != null && (
                  <span className="text-muted fs-6">
                    {bestPercent}% above best
                  </span>
                )}
                {worstPercent != null && (
                  <span className="text-muted fs-6">
                    {worstPercent}% better than worst
                  </span>
                )}
              </div>
            </div>
          </Col>

          {/* SECTION 3: Four Metrics (Best, Next Best, Worst, Next Worst) */}
          <Col xs={12} md={6}>
            <div className="d-flex flex-column flex-md-row flex-wrap align-items-start gap-2 gap-md-3 w-100">
              {getBestBetterValues(item)}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};


const RankSummary = () => {
  const config = getConfig();
  const { accessToken, user, isSystemAdmin, clientId: authClientId } = useAuth();
  const secureId = user?.sub;   // 👈 this is your SecureId
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [bestMarkets, setBestMarkets] = useState([]);
  const [worstMarkets, setWorstMarkets] = useState([]);
  const [markets, setMarkets] = useState([]);

  const [licenses, setLicenses] = useState([]);
  const defaultClientId =
    authClientId || config?.DefaultClientId || "6BD2C20F-0CE3-4B00-ACDE-63569A4273A1";
  // View As selection: "None" by default, specific clientId when chosen
  const [viewAsValue, setViewAsValue] = useState("None");

  // View As dropdown options
  const [viewAsOptions, setViewAsOptions] = useState([]);
  const [viewAsLoading, setViewAsLoading] = useState(false);

  // Load View As options for system admins
  useEffect(() => {
    if (!accessToken || !isSystemAdmin) return;

    let isCancelled = false;
    const loadViewAsOptions = async () => {
      try {
        setViewAsLoading(true);
        const list = await fetchSuppliersListApi({ userToken: accessToken });
        if (isCancelled) return;
        const mapped = (Array.isArray(list) ? list : []).map((item) => ({
          value: item.value,
          label: item.name,
        }));
        // Prepend a "None" option like pricing page
        setViewAsOptions([{ value: "None", label: "None" }, ...mapped]);
      } catch (e) {
        console.error("Error loading View As options", e);
        if (!isCancelled) {
          setViewAsOptions([]);
        }
      } finally {
        if (!isCancelled) setViewAsLoading(false);
      }
    };

    loadViewAsOptions();
    return () => {
      isCancelled = true;
    };
  }, [accessToken, isSystemAdmin]);

  // Compute effective clientId from View As selection
  const effectiveClientId =
    viewAsValue && viewAsValue !== "None" && viewAsValue !== "current"
      ? viewAsValue
      : defaultClientId;

  // Reload leaderboard/best/worst/licenses whenever effective client changes
  useEffect(() => {
    if (!accessToken || !effectiveClientId) return;

    const load = async () => {
      try {
        setLoading(true);
        // 1) Leaderboard
        const marketsRes = await fetchLeaderboardParsed(accessToken, effectiveClientId);
        setMarkets(marketsRes);

        // 2) Build ofN lookup from leaderboard for best/worst adornment
        const ofNLookup = buildOfNLookup(marketsRes || []);

        // 3) Best/Worst
        const [bestRes, worstRes] = await Promise.all([
          fetchBestMarketsParsed(accessToken, effectiveClientId, ofNLookup),
          fetchWorstMarketsParsed(accessToken, effectiveClientId, ofNLookup),
        ]);
        setBestMarkets(bestRes.slice(0, 10));
        setWorstMarkets(worstRes.slice(0, 10));

        // 4) Licenses
        const lic = await fetchLicenses(accessToken, effectiveClientId);
        setLicenses(lic);
      } catch (e) {
        setError(e.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [accessToken, effectiveClientId]);

  const [followedCompanies, setFollowedCompanies] = useState([]);
  const FOLLOWED_EVENTS_CLIENT_ID = isSystemAdmin
    ? defaultClientId
    : config.DefaultClientId;
  useEffect(() => {
    const loadFollowed = async () => {
      const data = await fetchFollowedCompanies(accessToken, FOLLOWED_EVENTS_CLIENT_ID, user?.sub, config.ApiBaseUrl);
      setFollowedCompanies(data);
    };
    if (user?.sub) loadFollowed();
  }, [accessToken, FOLLOWED_EVENTS_CLIENT_ID, user?.sub, config.ApiBaseUrl]);


  if (loading)
    return (
      <>
        {/* <h4 className="text-center">Loading Market Data...</h4> */}
        <div className="d-flex justify-content-center align-items-center mt-5 mb-5 loader-container">
          <img src={loader} className="loader-img" alt="Loading" />
        </div>
        <hr />
      </>
    );

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <Container fluid className="p-4">
      {/* Header row */}
      <Row className="mb-4 min-h-200">
        <Col md={9}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex flex-row align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                <Card.Title className="mb-0">My Licenses</Card.Title>
                {false && (
                  <div className="d-flex align-items-center gap-2 flex-nowrap">
                    <span className="mb-0 fw-bold" style={{ whiteSpace: "nowrap" }}>
                      View as
                    </span>
                    <div style={{ minWidth: 160 }}>
                      <Select
                        value={
                          viewAsOptions.find((opt) => opt.value === viewAsValue) ||
                          null
                        }
                        options={viewAsOptions}
                        onChange={(option) =>
                          setViewAsValue(option?.value || "None")
                        }
                        isDisabled={viewAsLoading}
                        isSearchable={false}
                        isClearable={false}
                      />
                    </div>
                  </div>
                )}
              </div>
              <LicenseList licenses={licenses} limit={5} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>My License Activity</Card.Title>
              <FollowedEvents events={followedCompanies} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Best/Worst */}
      <Row className="mb-4">
        <Col md={6}>
          <h5 className="mb-3">Best Utilities Today</h5>
          {bestMarkets.length === 0 ? (
            <div className="text-muted">No data available.</div>
          ) : (
            bestMarkets.map((m, i) => (
              <MarketPillCard key={`${m.utility}-${m.supplierId || i}`} item={m} type={"BEST"} variant="success" />
            ))
          )}
        </Col>
        <Col md={6}>
          <h5 className="mb-3">Worst Utilities Today</h5>
          {worstMarkets.length === 0 ? (
            <div className="text-muted">No data available.</div>
          ) : (
            worstMarkets.map((m, i) => (
              <MarketPillCard key={`${m.utility}-${m.supplierId || i}`} item={m} type={"WORST"} variant="danger" />
            ))
          )}
        </Col>
      </Row>

      {/* My Markets (context = current supplier) */}
      <Row>
        <Col>
          <h5>My Utilities Today</h5>
          {markets.map((market, idx) => {
            const engie = market.suppliers.find((s) => s.isEngie);
            if (!engie) return null;

            const top3 = market.suppliers.slice(0, 3);
            const worst = market.suppliers[market.suppliers.length - 1] || null;
            const pct = market.ofN > 1 ? ((engie.rank - 1) / (market.ofN - 1)) * 100 : 0;

            return (
              <Card className="mb-3" key={`${market.utility}-${idx}`}>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={4}>
                      <div className="d-flex align-items-center gap-2">
                        {getCommodityIcon(market.commodity)}
                        <p className="card-text mb-0 text-muted">
                          {/* State: <strong>{market.state}</strong> */}
                          {/* <br /> */}
                          Utility: <strong>{market.utility}</strong>
                        </p>
                      </div>
                    </Col>

                    <Col md={8}>
                      <div className="d-flex justify-content-end align-items-center gap-3 flex-wrap">
                        {/* Top 3 */}
                        {top3.map((s, idx2) => (
                          <div className="d-flex flex-column align-items-center minw-60" key={`${s.supplierId}-${idx2}`}>
                            <FontAwesomeIcon
                              icon={faMedal}
                              className={`medal-icon ${idx2 === 0 ? "medal-gold" : idx2 === 1 ? "medal-silver" : "medal-bronze"}`}
                            />
                            <div className="logo-frame">
                              <SupplierLogo supplierObject={s} size={45} />
                            </div>
                          </div>
                        ))}

                        {/* Me */}
                        <div className="d-flex flex-column align-items-center minw-60">
                          <span className="fw-bolder fs-5">#{engie.rank}</span>
                          <div className="logo-frame">
                            <SupplierLogo supplierObject={engie} fallback="engie.com" size={45} />
                          </div>
                        </div>

                        {/* Worst */}
                        {worst && (
                          <div className="d-flex flex-column align-items-center minw-60">
                            <FontAwesomeIcon icon={faPoo} className="poo-icon" />
                            <div className="logo-frame">
                              <SupplierLogo supplierObject={worst} size={45} />
                            </div>
                          </div>
                        )}

                        {/* Rank slider (disabled) */}
                        <div className="d-flex flex-column align-items-center minw-200 position-relative rank-range-wrap">
                          <div className="rank-bar-ends d-flex justify-content-between w-100">
                            <small>1</small>
                            <small>{market.ofN}</small>
                          </div>

                          <Form.Range
                            min={1}
                            max={market.ofN}
                            value={engie.rank}
                            disabled
                            className="form-range rank-range"
                          />

                          <div className="engie-rank-label" style={{ left: `${pct}%` }}>
                            {engie.rank}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </Col>
      </Row>
    </Container>
  );
};



export default RankSummary;
